import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import './DateRangeSelector.css';
import { DayPickerRangeController } from 'react-dates';
import { Button, Popover, PopoverBody } from 'reactstrap';
import { getReactDatesDefaults } from '../../Scss/Custom/ReactDates/ReactDatesChameleon';
import { DateFilters, DEEP_SKY_BLUE, START_DATE } from '../../Utils/Constants';
import { ResponsiveModal } from '@getgo/chameleon-react';
import {
  trackAnalyticsFeature,
  trackAnalyticsGenerateReportFeature
} from '../../Telemetry/Mixpanel/Features/analyticsTracker';
import { CALENDAR, VALUE_ADD } from '../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import SlideMenu from '../../Components/SlideMenu/SlideMenu';
import 'react-dates/initialize';
import { TimeRange } from '../../Utils/Constants';

class DateRangeSelector extends Component {
  static propTypes = {
    reactDatesProps: PropTypes.object,
    uniqueId: PropTypes.string.isRequired,
    onApply: PropTypes.func.isRequired,
    fromDateTime: PropTypes.string,
    toDateTime: PropTypes.string,
    isBig: PropTypes.bool.isRequired,
    futureDates: PropTypes.bool,
    disabled: PropTypes.bool,
    mixpanelData: PropTypes.shape({
      action: PropTypes.string.isRequired,
      reportType: PropTypes.string
    })
  };
  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      focusedInput: START_DATE,
      startDate: null,
      endDate: null,
      clickCount: 0,
      resetValid: false,
      isDateRangeSelectorOpen: false,
      predefinedDateFilterType: TimeRange.PREVIOUS
    };
  }
  render() {
    return this.props.isBig ? this.renderDesktopCalendar() : this.renderMobileCalendar();
  }

  renderDesktopCalendar = () => {
    const { uniqueId, disabled, fromDateTime, toDateTime, futureDates } = this.props;
    const { isDateRangeSelectorOpen, predefinedDateFilterType } = this.state;

    const { t } = this.context;
    const startDateString = fromDateTime !== null ? moment(fromDateTime).format(t('formatting.date')) : null;
    const endDateString = toDateTime !== null ? moment(toDateTime).format(t('formatting.date')) : null;
    const selectedBorderStyle = {
      borderBottom: `4px solid ${DEEP_SKY_BLUE}`,
      paddingBottom: '10px'
    };
    return (
      <div>
        <Button
          id={uniqueId}
          color="link"
          className={'dateRangeSelectorButton'}
          disabled={disabled}
          onClick={this.toggle}>
          {`${startDateString} - ${endDateString}`}
        </Button>
        <Popover
          isOpen={isDateRangeSelectorOpen}
          placement="bottom"
          target={uniqueId}
          toggle={this.toggle}
          hideArrow={true}
          className="dateRangeSelectorCalendarPopup">
          <PopoverBody>
            <div className="container">
              <div className="row">
                <div className="col-md-3 navButtonsContainer">
                  {futureDates && (
                    <div className="row dateMenu">
                      <div
                        className="col-sm-6"
                        style={predefinedDateFilterType === TimeRange.FUTURE ? null : selectedBorderStyle}
                        onClick={() => this.chooseDateRange(predefinedDateFilterType)}>
                        {t('dateFilter.previousDates')}
                      </div>
                      <div
                        className="col-sm-6"
                        style={predefinedDateFilterType === TimeRange.PREVIOUS ? null : selectedBorderStyle}
                        onClick={() => this.chooseDateRange(predefinedDateFilterType)}>
                        {t('dateFilter.upcomingDates')}
                      </div>
                    </div>
                  )}
                  <div
                    className="nav flex-column nav-pills navItems"
                    id="v-pills-tab"
                    role="tablist"
                    aria-orientation="vertical">
                    {this.getPredefinedDateRangeButtons()}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="dateRangeDefault">{this.getDateRangePicker(false)}</div>
                </div>
              </div>
            </div>
          </PopoverBody>
        </Popover>
      </div>
    );
  };

  renderMobileCalendar = () => {
    const { fromDateTime, toDateTime, disabled } = this.props;
    let { startDate, endDate, isDateRangeSelectorOpen } = this.state;
    const { t } = this.context;
    let startDateString = startDate ? startDate : fromDateTime;
    let endDateString = endDate ? endDate : toDateTime;
    let predefinedDatesArray = DateRangeSelector.predefinedDates({ t }, false);
    return (
      <div>
        <i
          className={`togo-icon togo-icon-calendar dateRangeSelectorMobileButton ${disabled ? 'disabledIcons' : ''}`}
          onClick={() => !disabled && this.toggle()}
        />
        <ResponsiveModal isOpen={isDateRangeSelectorOpen} onClose={this.toggle}>
          <section className="dateRangeSelectorCalendarModal">
            <div className="dateFilterHeader">
              <h3>{t('dateFilter.selectDates')}</h3>
            </div>
            <div>
              <SlideMenu menuItems={predefinedDatesArray} onClick={this.applyCustomisedDateFilter} />
            </div>
            <div className="dateFilterRange">
              <span>{moment(startDateString).format(t('formatting.date'))}</span>
              <span className="timeDisplay">{t('dateFilter.to').toLowerCase()}</span>
              <span>{moment(endDateString).format(t('formatting.date'))}</span>
            </div>

            <div className="dayPickerRangeController">{this.getDateRangePicker(true)}</div>

            <div className="btnAlignment">
              <Button color="primary" onClick={() => this.applyDateFilter(startDateString, endDateString, 0)}>
                {t('buttons.applyDates')}
              </Button>
            </div>
          </section>
        </ResponsiveModal>
      </div>
    );
  };

  toggle = () => {
    this.setState(prevState => ({
      resetValid: true,
      clickCount: 0,
      focusedInput: START_DATE,
      isDateRangeSelectorOpen: !prevState.isDateRangeSelectorOpen
    }));
  };

  getPredefinedDateRangeButtons = () => {
    const { t } = this.context;
    const { predefinedDateFilterType } = this.state;
    let predefinedDatesArray = DateRangeSelector.predefinedDates(
      { t },
      this.props.futureDates,
      predefinedDateFilterType
    );
    return predefinedDatesArray.map(item => (
      <div className="navigationPills" key={item.id}>
        <Button
          key={item.id}
          className="navButtons"
          onClick={() => this.applyCustomisedDateFilter(item.id)}
          color="link">
          {item.label}
        </Button>
      </div>
    ));
  };

  chooseDateRange = range => {
    if (range === TimeRange.PREVIOUS) {
      this.setState({
        predefinedDateFilterType: TimeRange.FUTURE
      });
    } else {
      this.setState({
        predefinedDateFilterType: TimeRange.PREVIOUS
      });
    }
  };

  applyCustomisedDateFilter = option => {
    const endOfDayString = moment()
      .endOf('d')
      .toISOString();
    let startDateString, endDateString;
    switch (option) {
      case DateFilters.NEXT_YEAR:
        startDateString = endOfDayString;
        endDateString = moment()
          .add(1, 'y')
          .startOf('d')
          .toISOString();
        break;
      case DateFilters.NEXT_HUNDRED_EIGHTY_DAYS:
        startDateString = endOfDayString;
        endDateString = moment()
          .add(179, 'd')
          .startOf('d')
          .toISOString();
        break;
      case DateFilters.NEXT_NINETY_DAYS:
        startDateString = endOfDayString;
        endDateString = moment()
          .add(89, 'd')
          .startOf('d')
          .toISOString();
        break;
      case DateFilters.NEXT_THIRTY_DAYS:
        startDateString = endOfDayString;
        endDateString = moment()
          .add(29, 'd')
          .startOf('d')
          .toISOString();
        break;
      case DateFilters.NEXT_SEVEN_DAYS:
        startDateString = endOfDayString;
        endDateString = moment()
          .add(6, 'd')
          .startOf('d')
          .toISOString();
        break;
      case DateFilters.TODAY:
        startDateString = moment()
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_SEVEN_DAYS:
        startDateString = moment()
          .subtract(6, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_THIRTY_DAYS:
        startDateString = moment()
          .subtract(29, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_NINETY_DAYS:
        startDateString = moment()
          .subtract(89, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_HUNDRED_EIGHTY_DAYS:
        startDateString = moment()
          .subtract(179, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_YEAR:
        startDateString = moment()
          .subtract(1, 'y')
          .add(1, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      case DateFilters.LAST_TWO_YEARS:
        startDateString = moment()
          .subtract(2, 'y')
          .add(1, 'd')
          .startOf('d')
          .toISOString();
        endDateString = endOfDayString;
        break;
      default:
        return null;
    }
    this.applyDateFilter(startDateString, endDateString);
  };

  getDateRangePicker = isMobile => {
    const { reactDatesProps = {}, fromDateTime, toDateTime } = this.props;
    let { startDate, endDate, focusedInput, clickCount, resetValid } = this.state;
    let countOfClicks = clickCount;
    if (resetValid && !isMobile) {
      startDate = fromDateTime;
      endDate = toDateTime;
    }
    if ((startDate === null && fromDateTime === null) || (endDate === null && toDateTime === null)) {
      return null;
    }

    return (
      <DayPickerRangeController
        {...getReactDatesDefaults(isMobile)}
        {...reactDatesProps}
        ref={this.setDayPickerRef}
        startDate={startDate ? moment(startDate) : moment(fromDateTime)}
        endDate={endDate ? moment(endDate) : moment(toDateTime)}
        focusedInput={focusedInput}
        onFocusChange={focusedInput => this.setState({ focusedInput: focusedInput || 'startDate' })}
        onDatesChange={({ startDate: newStartDate, endDate: newEndDate }) => {
          if (newEndDate === null) {
            newEndDate = moment();
          }
          if (newStartDate && newEndDate) {
            newStartDate = newStartDate.clone().startOf('day');
            newEndDate = newEndDate.clone().endOf('day');
            if (newEndDate.isBefore(newStartDate)) {
              newEndDate = newStartDate.clone().endOf('day');
            }
            ++countOfClicks;
            this.setState({
              startDate: newStartDate.toISOString(),
              endDate: newEndDate.toISOString(),
              clickCount: countOfClicks,
              resetValid: false
            });

            if (countOfClicks >= 2 && !isMobile) {
              countOfClicks = 0;
              this.applyDateFilter(newStartDate.toISOString(), newEndDate.toISOString(), countOfClicks);
            }
          }
        }}
      />
    );
  };

  applyDateFilter = (startDate, endDate, clickCount) => {
    const { onApply, isBig, mixpanelData } = this.props;
    trackAnalyticsFeature(VALUE_ADD, mixpanelData.action, CALENDAR);
    if (mixpanelData.reportType) {
      trackAnalyticsGenerateReportFeature(mixpanelData.reportType, CALENDAR);
    }
    if (isBig) {
      this.setState({
        clickCount: clickCount,
        startDate: startDate,
        endDate: endDate,
        resetValid: true
      });
    }
    onApply(startDate, endDate);
    this.toggle();
  };

  static predefinedDates({ t }, includeFutureDates, predefinedDateFilterType) {
    let defaultFilters = [
      {
        id: DateFilters.TODAY,
        label: t('dateFilter.today')
      },
      {
        id: DateFilters.LAST_SEVEN_DAYS,
        label: t('dateFilter.previousSevenDays')
      },
      {
        id: DateFilters.LAST_THIRTY_DAYS,
        label: t('dateFilter.previousThirtyDays')
      },
      {
        id: DateFilters.LAST_NINETY_DAYS,
        label: t('dateFilter.previousNinetyDays')
      },
      {
        id: DateFilters.LAST_HUNDRED_EIGHTY_DAYS,
        label: t('dateFilter.previousHundredEightyDays')
      },
      {
        id: DateFilters.LAST_YEAR,
        label: t('dateFilter.previousYear')
      },
      {
        id: DateFilters.LAST_TWO_YEARS,
        label: t('dateFilter.previousTwoYears')
      }
    ];
    let nextDateFilter = [
      {
        id: DateFilters.TODAY,
        label: t('dateFilter.today')
      },
      {
        id: DateFilters.NEXT_SEVEN_DAYS,
        label: t('dateFilter.nextSevenDays')
      },
      {
        id: DateFilters.NEXT_THIRTY_DAYS,
        label: t('dateFilter.nextThirtyDays')
      },
      {
        id: DateFilters.NEXT_NINETY_DAYS,
        label: t('dateFilter.nextNinetyDays')
      },
      {
        id: DateFilters.NEXT_HUNDRED_EIGHTY_DAYS,
        label: t('dateFilter.nextHundredEightyDays')
      },
      {
        id: DateFilters.NEXT_YEAR,
        label: t('dateFilter.nextYear')
      }
    ];

    return includeFutureDates && predefinedDateFilterType === TimeRange.FUTURE ? nextDateFilter : defaultFilters;
  }
}

export default DateRangeSelector;



// WEBPACK FOOTER //
// ./src/Components/DateRangeSelector/DateRangeSelector.js